// Add logging before class definition
console.log('Starting VariantSelector script load');

class VariantSelector extends HTMLElement {
  constructor() {
    super();
    console.log('VariantSelector: Initializing');

    // Add error handling for initialization
    try {
      this.form = this.querySelector('.variant-options');
      if (!this.form) {
        console.error('VariantSelector: Could not find .variant-options');
        return;
      }

      this.buttons = this.form.querySelectorAll('.option-button');
      this.selects = this.form.querySelectorAll('.option-select');
      this.addToCartButton = this.querySelector('.add-to-cart-button');

      // Check if dataset exists
      if (!this.dataset.variants) {
        console.error('VariantSelector: No variants data found in dataset');
        return;
      }

      this.variants = JSON.parse(this.dataset.variants);
      this.optionsCount = parseInt(this.dataset.optionsCount);
      this.addToCartText = this.dataset.addToCartText;

      console.log('VariantSelector: Found elements', {
        form: !!this.form,
        buttons: this.buttons.length,
        selects: this.selects.length,
        variants: this.variants.length,
        optionsCount: this.optionsCount,
        addToCartButton: !!this.addToCartButton,
      });

      this.quantityInput = this.querySelector('[data-quantity-input]');
      this.initializeButtons();
      this.initializeQuantityButtons();
      this.initializeAddToCart();
    } catch (error) {
      console.error('VariantSelector: Error during initialization', error);
    }
  }

  connectedCallback() {
    // Component is now in the DOM
    console.log('VariantSelector: Connected to DOM');
  }

  initializeButtons() {
    console.log('VariantSelector: Initializing button and select listeners');

    // Initialize button clicks
    this.buttons.forEach((button) => {
      button.addEventListener('click', (e) => {
        console.log('VariantSelector: Button clicked', {
          value: button.dataset.value,
          position: button.dataset.optionPosition,
        });
        this.handleOptionClick(e);
      });
    });

    // Initialize select changes
    this.selects.forEach((select) => {
      select.addEventListener('change', (e) => {
        console.log('VariantSelector: Select changed', {
          value: select.value,
          position: select.dataset.optionPosition,
        });
        this.handleSelectChange(e);
      });
    });
  }

  handleOptionClick(e) {
    const button = e.target;
    const optionPosition = button.dataset.optionPosition;
    console.log('VariantSelector: Handling option click', {
      position: optionPosition,
      value: button.dataset.value,
    });

    // Update button states in this option group
    const siblings = this.form.querySelectorAll(`[data-option-position="${optionPosition}"]`);
    siblings.forEach((sibling) => {
      sibling.classList.remove('tw-border-black');
      sibling.classList.add('tw-border-gray-300');
    });
    button.classList.remove('tw-border-gray-300');
    button.classList.add('tw-border-black');

    this.updateVariantSelection();
  }

  handleSelectChange(e) {
    const select = e.target;
    const optionPosition = select.dataset.optionPosition;
    const value = select.value;

    this.updateVariantSelection();
  }

  updateVariantSelection() {
    const selectedOptions = [];
    for (let i = 1; i <= this.optionsCount; i++) {
      const selectedButton = this.form.querySelector(`[data-option-position="${i}"].tw-border-black`);
      const selectedOption = this.form.querySelector(`select[data-option-position="${i}"]`);

      if (selectedButton) {
        selectedOptions.push(selectedButton.dataset.value);
      } else if (selectedOption) {
        selectedOptions.push(selectedOption.value);
      }
    }

    console.log('VariantSelector: Selected options', selectedOptions);

    const selectedVariant = this.variants.find((variant) => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });

    console.log('VariantSelector: Found variant', selectedVariant);

    if (selectedVariant) {
      this.updateAddToCartButton(selectedVariant.available);
      window.history.replaceState({}, '', `?variant=${selectedVariant.id}`);

      // Update add to cart button to use selected variant
      this.addToCartButton.dataset.variantId = selectedVariant.id;

      // Dispatch custom event for variant change
      this.dispatchEvent(
        new CustomEvent('variant:changed', {
          detail: { variant: selectedVariant },
          bubbles: true,
        })
      );
    }
  }

  initializeQuantityButtons() {
    const minusButton = this.querySelector('button[name="minus"]');
    const plusButton = this.querySelector('button[name="plus"]');

    if (minusButton && plusButton && this.quantityInput) {
      minusButton.addEventListener('click', () => this.updateQuantity('decrease'));
      plusButton.addEventListener('click', () => this.updateQuantity('increase'));
      this.quantityInput.addEventListener('change', () => this.validateQuantity());
    }
  }

  updateQuantity(action) {
    const currentValue = parseInt(this.quantityInput.value);
    const min = parseInt(this.quantityInput.min) || 1;
    const max = parseInt(this.quantityInput.max) || Infinity;
    const step = parseInt(this.quantityInput.step) || 1;

    let newValue = currentValue;
    if (action === 'increase') {
      newValue = Math.min(currentValue + step, max);
    } else {
      newValue = Math.max(currentValue - step, min);
    }

    this.quantityInput.value = newValue;
    this.addToCartButton.dataset.quantity = newValue;
    this.validateQuantity();
  }

  validateQuantity() {
    let value = parseInt(this.quantityInput.value);
    const min = parseInt(this.quantityInput.min) || 1;
    const max = parseInt(this.quantityInput.max) || Infinity;
    const step = parseInt(this.quantityInput.step) || 1;

    if (isNaN(value)) value = min;
    if (value < min) value = min;
    if (value > max) value = max;

    // Ensure value adheres to step
    value = Math.round(value / step) * step;

    this.quantityInput.value = value;
  }

  initializeAddToCart() {
    console.log('VariantSelector: Initializing add to cart handler');
    this.addToCartButton.addEventListener('click', async (e) => {
      e.preventDefault();
      await this.handleAddToCart();
    });
  }

  async handleAddToCart() {
    const variantId = this.addToCartButton.dataset.variantId;
    const quantity = parseInt(this.quantityInput?.value) || 1;

    if (window.slideoutCart) {
      this.addToCartButton.disabled = true;
      try {
        await window.slideoutCart.addToCart(variantId, quantity);
      } catch (error) {
        console.error('VariantSelector: Error adding to cart:', error);
      } finally {
        this.addToCartButton.disabled = false;
      }
    }
  }

  updateAddToCartButton(available) {
    console.log('VariantSelector: Updating add to cart button', { available });
    this.addToCartButton.disabled = !available;
    this.addToCartButton.textContent = available ? this.addToCartText : 'Sold Out';
  }
}

// Add check for proper script loading
try {
  customElements.define('variant-selector', VariantSelector);
  console.log('VariantSelector: Custom element defined successfully');

  // Add a check to see if any elements exist
  document.addEventListener('DOMContentLoaded', () => {
    const selectors = document.querySelectorAll('variant-selector');
    console.log('VariantSelector: Found elements in DOM', {
      count: selectors.length,
      elements: selectors,
    });
  });
} catch (error) {
  console.error('VariantSelector: Error defining custom element', error);
}
